import { ArgumentException } from '../../../exceptions.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { GiftCardTemplate } from '../gift-card/gift-card-template.js';
import { GiftCardVariation } from '../gift-card/gift-card-variation.js';
import { Price } from '../price.js';
import { CartItemDto } from './cart-item.js';

export class GiftCardCartItemDto extends CartItemDto {
  static readonly TYPE_ID = 'd28e0ea09d4441c38fb7e5aa9f02cb7a';
  static readonly SCHEMA_VERSION = 1;

  public readonly originalUnitPrice: Price | null = null;
  public readonly unitPrice: Price;

  private _variation: GiftCardVariation;
  private _totalPrice: Price;

  private constructor(
    public readonly template: GiftCardTemplate,
    public readonly variationId: string,
    public readonly quantity: number,
    unitPrice: Price | null,
  ) {
    super();

    const variation = template.findVariationById(variationId);
    if(variation == null) {
      throw new ArgumentException('Gift card variation ID not valid.');
    }

    this.unitPrice = (unitPrice ?? variation.price).clone();
    this._variation = variation;
    this._totalPrice = this.unitPrice.scale(quantity);
  }

  get uniqueId(): string {
    return this.template.uniqueId;
  }

  get templateId(): string {
    return this.template.uniqueId;
  }

  get totalPrice(): Price {
    return this._totalPrice;
  }

  get variation(): GiftCardVariation {
    return this._variation;
  }

  get $typeId(): string {
    return GiftCardCartItemDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(GiftCardCartItemDto.SCHEMA_VERSION);
  }

  private static create(args: {
    template: GiftCardTemplate,
    variationId: string,
    quantity: number,
    unitPrice?: Price,
  }): GiftCardCartItemDto {
    return new GiftCardCartItemDto(
        args.template,
        args.variationId,
        args.quantity,
        args.unitPrice ?? null,
    );
  }

  static online(args: {
    template: GiftCardTemplate,
    variationId: string,
    quantity: number,
  }): GiftCardCartItemDto {
    return GiftCardCartItemDto.create(args);
  }

  static offline(args: {
    template: GiftCardTemplate,
    variationId: string,
    quantity: number,
    unitPrice?: Price,
  }): GiftCardCartItemDto {
    return GiftCardCartItemDto.create(args);
  }

  static fromMap(context: SerializationContext, map: SerializedData): GiftCardCartItemDto {
    return this.create({
      template: context.fromData<GiftCardTemplate>(MapUtils.getRecord(map, 'template')),
      variationId: MapUtils.getString(map, 'variationId'),
      quantity: MapUtils.getInteger(map, 'quantity'),
      unitPrice: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'unitPrice')) ?? undefined,
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'template': context.toData(this.template),
      'variationId': this.variationId,
      'quantity': this.quantity,
      'unitPrice': this.unitPrice.encode(),
    };
  }

  clone(): this {return GiftCardCartItemDto.create({ ...this, template: this.template.clone() }) as this;}

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(this.template, other.template) &&
      this.variationId === other.variationId &&
      this.quantity === other.quantity &&
      isSameData(this.unitPrice, other.unitPrice);
  }

  static verifyType(model: DataModel): boolean {return model instanceof GiftCardCartItemDto;}
  static assertType(model: DataModel): model is GiftCardCartItemDto {return model instanceof GiftCardCartItemDto;}
}

export class GiftCardCartItemDtoBuilder extends DataModelBuilder<GiftCardCartItemDto> {
  build(context: SerializationContext, map: SerializedData): GiftCardCartItemDto {
    return GiftCardCartItemDto.fromMap(context, map);
  }
}
